import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject, Injector, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxModalComponent,
  OnyxTextareaComponent,
  onyxTextareaLengthValidator,
  OnyxToastService,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { ValidationHelper } from '../../../../../../../../common/helpers/validation.helper';
import { OrderRejectReason } from '../../../../../../common/enums/order-reject-reason';
import { OrdersService } from '../../../../../../common/services/orders.service';

export enum OtherReasonModalMode {
  REJECT = 'reject',
  CHANGE = 'change',
}

export interface OtherReasonModalData {
  mode: OtherReasonModalMode;
  orderUuid: string;
  vehicleUuid: string;
}

@Component({
  selector: 'app-other-reason-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    OnyxTextareaComponent,
    ReactiveFormsModule,
    OnyxButtonComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './other-reason-modal.component.html',
  styleUrl: './other-reason-modal.component.scss',
})
export class OtherReasonModalComponent {
  protected readonly data = inject<OtherReasonModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<boolean>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly ordersService = inject(OrdersService);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'orders.orderModal.rejectDropdown';

  protected reason = this.fb.control<string | null>(null, [
    Validators.required,
    onyxTextareaLengthValidator,
  ]);

  protected loading = signal(false);
  protected close$ = new Subject<boolean | undefined>();

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.reason, this.injector))) {
      return;
    }

    const form = this.reason.getRawValue();

    this.loading.set(true);
    this.ordersService
      .rejectVehicle(this.data.orderUuid, {
        vehicleUuid: this.data.vehicleUuid,
        reason: OrderRejectReason.OTHER,
        other: form!,
      })
      .subscribe(() => {
        const isReject = this.data.mode === OtherReasonModalMode.REJECT;
        if (isReject) {
          this.toastService.showInformation({
            message: 'orders.orderModal.toasts.vehicleRejected',
          });
        }

        this.close$.next(!isReject ? true : undefined);
      })
      .add(() => this.loading.set(false));
  }
}
